import {
  resolveWearablePathOverride,
  type WearablePathOverrideEntry,
} from "./wearables.pathOverrideMemory";
import type {
  WearablePathDefaults,
  WearablePathTrack,
} from "./wearables.pathDefaults";
import type { WearablesRouteSurface } from "./wearables.routeCompanion";

export type WearablesRecommendation = {
  mode: WearablePathTrack;
  label: string;
  subLabel: string;
  destination: string;
  confidence: number;
  alternatives: WearablePathTrack[];
};

type SurfaceMeta = {
  surface: WearablesRouteSurface;
  label: string;
  href: string;
};

const ALL_TRACKS: WearablePathTrack[] = [
  "creator",
  "xr",
  "readiness",
  "recovery",
  "general",
];

const SURFACE_META: Record<WearablesRouteSurface, SurfaceMeta> = {
  "guided-studio": {
    surface: "guided-studio",
    label: "Guided Studio",
    href: "/guided-studio",
  },
  omnistudio: {
    surface: "omnistudio",
    label: "OmniStudio",
    href: "/omnistudio",
  },
  xr: {
    surface: "xr",
    label: "XR",
    href: "/guided/start-from-world",
  },
};

function titleCaseTrack(track: WearablePathTrack): string {
  switch (track) {
    case "xr":
      return "XR";
    case "general":
      return "General";
    default:
      return track.charAt(0).toUpperCase() + track.slice(1);
  }
}

function resolveSurface(pathname: string): SurfaceMeta | null {
  if (pathname === "/guided-studio") {
    return SURFACE_META["guided-studio"];
  }

  if (pathname === "/omnistudio") {
    return SURFACE_META.omnistudio;
  }

  if (pathname === "/guided/start-from-world" || pathname === "/xr") {
    return SURFACE_META.xr;
  }

  return null;
}

function resolveRecommendationDestination(track: WearablePathTrack): SurfaceMeta {
  switch (track) {
    case "creator":
      return SURFACE_META.omnistudio;
    case "xr":
      return SURFACE_META.xr;
    case "readiness":
    case "recovery":
    case "general":
    default:
      return SURFACE_META["guided-studio"];
  }
}

export function buildWearablesRecommendation(args: {
  pathname: string;
  search: string;
  baseDefaults: WearablePathDefaults;
  overrideEntry?: WearablePathOverrideEntry | null;
}): WearablesRecommendation | null {
  const surfaceMeta = resolveSurface(args.pathname);

  if (!surfaceMeta) {
    return null;
  }

  const searchParams = new URLSearchParams(args.search);
  const fromWearables = searchParams.get("entry") === "wearables";
  const resolved = resolveWearablePathOverride(args.baseDefaults, args.overrideEntry);
  const destination = resolveRecommendationDestination(resolved.effectiveTrack);
  const modeLabel = titleCaseTrack(resolved.effectiveTrack);

  return {
    mode: resolved.effectiveTrack,
    label: destination.label,
    subLabel: fromWearables
      ? `${modeLabel} continuity is active for ${surfaceMeta.label}.`
      : `${modeLabel} is the fastest next route from ${surfaceMeta.label}.`,
    destination: destination.href,
    confidence: resolved.effectiveTrack === "general" ? 0.56 : 0.64,
    alternatives: ALL_TRACKS.filter((track) => track !== resolved.effectiveTrack),
  };
}